import { useQuery, useQueryClient, useMutation, UseQueryResult, UseMutationResult } from '@tanstack/react-query';
import {
  requestProjectCellRoutes,
  requestProjectCellRoute,
  createProjectCellRouteRequest,
  updateProjectCellRouteRequest,
  deleteProjectCellRouteRequest,
} from '_/api/projects/projectCellRoutes.api';
import { IProjectCellRouteForm } from '_/types/projects/projectCellRoutes.types';
import { notification } from 'antd';

// Query keys
export const PROJECT_CELL_ROUTES_QUERY_KEY = (projectId: string): string[] => ['project', projectId, 'cellRoutes'];
export const PROJECT_CELL_ROUTE_QUERY_KEY = (cellRouteId: string): string[] => ['projectCellRoute', cellRouteId];

/**
 * Hook to fetch all project cell routes for a project
 */
export const useProjectCellRoutes = (projectId?: string): UseQueryResult<any> => {
  return useQuery({
    queryKey: PROJECT_CELL_ROUTES_QUERY_KEY(projectId || ''),
    queryFn: () => requestProjectCellRoutes(projectId!),
    enabled: !!projectId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

/**
 * Hook to fetch a single project cell route
 */
export const useProjectCellRoute = (cellRouteId?: string): UseQueryResult<any> => {
  return useQuery({
    queryKey: PROJECT_CELL_ROUTE_QUERY_KEY(cellRouteId || ''),
    queryFn: () => requestProjectCellRoute(cellRouteId!),
    enabled: !!cellRouteId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

/**
 * Hook to create a project cell route
 */
export const useCreateProjectCellRoute = (
  projectId?: string,
  onSuccessCallback?: () => void,
): UseMutationResult<any, Error, IProjectCellRouteForm> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (projectCellRoute: IProjectCellRouteForm) => createProjectCellRouteRequest(projectCellRoute),
    onSuccess: () => {
      // Invalidate project cell routes list
      if (projectId) {
        void queryClient.invalidateQueries({ queryKey: PROJECT_CELL_ROUTES_QUERY_KEY(projectId) });
      }
      notification.success({
        message: 'Project Cell Route Created',
      });
      onSuccessCallback?.();
    },
    onError: (error: any) => {
      console.error('Error creating project cell route', error);
      notification.error({
        message: error.messages?.[0] || 'Error creating project cell route',
      });
    },
  });
};

/**
 * Hook to update a project cell route
 */
export const useUpdateProjectCellRoute = (
  projectId?: string,
  cellRouteId?: string,
  onSuccessCallback?: () => void,
): UseMutationResult<any, Error, Partial<IProjectCellRouteForm>> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (changes: Partial<IProjectCellRouteForm>) => updateProjectCellRouteRequest(cellRouteId!, changes),
    onSuccess: () => {
      // Invalidate the specific cell route query
      if (cellRouteId) {
        void queryClient.invalidateQueries({ queryKey: PROJECT_CELL_ROUTE_QUERY_KEY(cellRouteId) });
      }
      // Invalidate project cell routes list
      if (projectId) {
        void queryClient.invalidateQueries({ queryKey: PROJECT_CELL_ROUTES_QUERY_KEY(projectId) });
      }
      notification.success({
        message: 'Project Cell Route Updated',
      });
      onSuccessCallback?.();
    },
    onError: (error: any) => {
      console.error('Error updating project cell route', error);
      notification.error({
        message: error.messages?.[0] || 'Error updating project cell route',
      });
    },
  });
};

/**
 * Hook to delete a project cell route
 */
export const useDeleteProjectCellRoute = (projectId?: string): UseMutationResult<any, Error, string> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (cellRouteId: string) => deleteProjectCellRouteRequest(cellRouteId),
    onSuccess: () => {
      // Invalidate project cell routes list
      if (projectId) {
        void queryClient.invalidateQueries({ queryKey: PROJECT_CELL_ROUTES_QUERY_KEY(projectId) });
      }
      notification.success({
        message: 'Project Cell Route Deleted',
      });
    },
    onError: (error: any) => {
      console.error('Error deleting project cell route', error);
      notification.error({
        message: error.messages?.[0] || 'Error deleting project cell route',
      });
    },
  });
};
